// Display server amounts verbatim. Never derive tax from prices, ZIP, or labels.
export function checkoutTotals(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const find = (codes, titles) =>
    list.find((row) => {
      if (row?.code) return codes.includes(row.code);
      const title = String(row?.title || "")
        .trim()
        .replace(/:$/, "")
        .toLowerCase();
      return titles.includes(title);
    });
  return {
    subtotal: find(["sub_total"], ["sub-total", "subtotal", "sub total"]),
    shipping: find(["shipping"], ["shipping"]),
    tax: find(["hooligans_tax", "tax"], ["sales tax", "tax"]),
    total: find(["total"], ["total"]),
  };
}

// Money arrives as display text ("$1,260.00", "-$33.00") or raw OpenCart
// values ("-33.0000", -33). Sums are done in integer cents so thousands
// separators and float rounding cannot skew a total.
export function moneyToCents(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? Math.round(value * 100) : null;
  }
  const normalized = String(value ?? "").replace(/[^0-9.-]+/g, "");
  if (!/^-?\d*\.?\d+$/.test(normalized)) return null;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
}

export function formatCents(cents, currency = null) {
  if (!Number.isInteger(cents)) return "";
  const left = currency?.symbol_left ?? (currency?.symbol_right ? "" : "$");
  const right = currency?.symbol_right ?? "";
  const amount = (Math.abs(cents) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${cents < 0 ? "-" : ""}${left}${amount}${right}`;
}

const parseData = (value) => {
  if (value && typeof value === "object") return value;
  try {
    const parsed = JSON.parse(String(value || ""));
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const isCartCouponRow = (row) =>
  String(row?.code || "").toLowerCase() === "coupon" ||
  Boolean(parseData(row?.data).coupon_code);

const normalizedTitle = (row) =>
  String(row?.title || "")
    .trim()
    .replace(/:$/, "")
    .toLowerCase();

// The checkout's updateOrder rows carry only title/text. The coupon row is the
// one titled like the cart's coupon total ("Coupon (WELCOME10)").
export function findCouponRow(orderRows = [], cartTotals = []) {
  const rows = Array.isArray(orderRows) ? orderRows : [];
  const byCode = rows.find(
    (row) => String(row?.code || "").toLowerCase() === "coupon"
  );
  if (byCode) return byCode;
  const cartCoupon = (Array.isArray(cartTotals) ? cartTotals : []).find(
    isCartCouponRow
  );
  if (cartCoupon) {
    const title = normalizedTitle(cartCoupon);
    const match = rows.find((row) => normalizedTitle(row) === title);
    if (match) return match;
  }
  return rows.find((row) => /^coupon\b/.test(normalizedTitle(row))) || null;
}

// Changes that move the order total: lines, quantities, the cart total and the
// applied coupon. The checkout refreshes its server totals when this changes.
export function cartTotalsSignature(cart) {
  if (!cart) return "";
  const lines = (Array.isArray(cart.products) ? cart.products : [])
    .map(
      (line) =>
        `${line?.key ?? line?.product?.id ?? ""}:${line?.quantity ?? ""}`
    )
    .join(",");
  const coupon = (Array.isArray(cart.totals) ? cart.totals : []).find(
    isCartCouponRow
  );
  return [
    lines,
    cart.total ?? "",
    coupon ? `${coupon.title}=${coupon.value}` : "",
  ].join("|");
}

const textRow = (row) =>
  row?.text ? { title: row.title || "", text: String(row.text) } : null;

// One source for every total the checkout shows. Server rows win once the
// order has been priced (address + shipping). Before that, after the cart
// changed on the information step ("stale"), or when re-pricing failed, the
// cart gives an estimate without shipping or tax.
export function displayTotals({
  orderTotals,
  cart,
  status = "fresh",
  currency = null,
} = {}) {
  const rows = Array.isArray(orderTotals) ? orderTotals : [];
  const cartTotals = Array.isArray(cart?.totals) ? cart.totals : [];
  if (rows.length && status !== "failed" && status !== "stale") {
    const found = checkoutTotals(rows);
    const coupon = findCouponRow(rows, cartTotals);
    const total = textRow(found.total);
    return {
      estimated: false,
      pending: status === "pending",
      failed: false,
      subtotal: textRow(found.subtotal),
      coupon: coupon
        ? { ...textRow(coupon), cents: moneyToCents(coupon.text) }
        : null,
      tax: textRow(found.tax),
      total: total ? { ...total, cents: moneyToCents(total.text) } : null,
    };
  }
  const cartCoupon = cartTotals.find(isCartCouponRow);
  const couponCents = cartCoupon ? moneyToCents(cartCoupon.value) : null;
  const totalText = cart?.total ? String(cart.total) : "";
  const subtotalText = cart?.subtotal ? String(cart.subtotal) : "";
  return {
    estimated: true,
    pending: status === "pending",
    failed: status === "failed",
    subtotal: subtotalText ? { title: "Sub-Total", text: subtotalText } : null,
    coupon:
      couponCents !== null
        ? {
            title: cartCoupon.title || "",
            text: formatCents(couponCents, currency),
            cents: couponCents,
          }
        : null,
    tax: null,
    total: totalText
      ? { title: "Total", text: totalText, cents: moneyToCents(totalText) }
      : null,
  };
}
